import {
  GROWTH_OPTION_PRESETS,
  DEFAULT_FORECAST_GROWTH_PHASES,
  applyGrowthOptionPreset,
  createDefaultGrowth,
  normalizeGrowth,
} from '../model/growthOptions';

export default function GrowthPanel({ growth, onChange }) {
  const g = normalizeGrowth(growth || createDefaultGrowth('traction'));
  const phases = g.phases?.length > 0
    ? g.phases
    : DEFAULT_FORECAST_GROWTH_PHASES.map((p) => ({ ...p }));

  const update = (patch) =>
    onChange(normalizeGrowth({ ...g, ...patch, preset: patch.preset || 'custom' }));

  const updatePhase = (index, patch) => {
    const next = phases.map((phase, i) => (i === index ? { ...phase, ...patch } : phase));
    update({ phases: next });
  };

  return (
    <section className="chart-card economics-panel">
      <h2>Growth options</h2>
      <p className="chart-hint">
        Phased <strong>organic</strong> signup growth (Fall flat / Traction / Explode). Does{' '}
        <strong>not</strong> scale with marketing budget — paid acquisition is separate (Channels,
        budget ÷ CAC). When phased is on, Channels organic %/mo is overridden. Results charts use
        MC P50 when Monte Carlo is on — edit phases, then check Results (or turn Mix regimes off
        on Scenarios so every run uses this table). Mean growth is the arithmetic monthly input;
        with multiplicative volatility, P50 can sit below the deterministic path while P90 carries
        the upside. Paid-plan mix drift (Basic→Premium, optional Premium→Enterprise) lives on
        Assumptions — these phases only grow signups.
      </p>

      <div className="economics-preset-row">
        {Object.entries(GROWTH_OPTION_PRESETS).map(([key, preset]) => (
          <button
            key={key}
            type="button"
            className={g.preset === key ? 'primary-button' : 'secondary-button'}
            onClick={() => onChange(applyGrowthOptionPreset(g, key))}
            title={preset.hint}
          >
            {preset.label}
          </button>
        ))}
      </div>
      {g.preset && GROWTH_OPTION_PRESETS[g.preset] && (
        <p className="chart-hint economics-muted">{GROWTH_OPTION_PRESETS[g.preset].hint}</p>
      )}

      <div className="economics-form-grid economics-form-grid-3">
        <label className="economics-field forecast-toggle">
          <input
            type="checkbox"
            checked={g.enabled !== false}
            onChange={(e) => update({ enabled: e.target.checked })}
          />
          <span>Phased growth enabled</span>
        </label>
        <label className="economics-field">
          <span>Mode</span>
          <select
            value={g.mode || 'phased'}
            onChange={(e) => update({ mode: e.target.value })}
          >
            <option value="phased">Phased (recommended)</option>
            <option value="channel">Use Channels organic % only</option>
          </select>
        </label>
        <label className="economics-field">
          <span>Distribution (MC)</span>
          <select
            value={g.distribution || 'lognormal'}
            onChange={(e) => update({ distribution: e.target.value })}
            disabled={g.enabled === false || g.mode === 'channel'}
          >
            <option value="lognormal">Lognormal — fat right tail</option>
            <option value="normal">Normal — symmetric</option>
            <option value="fixed">Fixed — phase mean only</option>
          </select>
        </label>
      </div>

      <h3 className="economics-subheading">Growth phases</h3>
      <p className="chart-hint">
        Through month = last month in phase. Early: high mean + high volatility → explode or fall
        flat. Middle: traction. Late: scale.
      </p>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Through month</th>
              <th>Mean growth %/mo</th>
              <th>Volatility %</th>
            </tr>
          </thead>
          <tbody>
            {phases.map((phase, index) => (
              <tr key={`${phase.label}-${index}`}>
                <td>
                  <input
                    className="economics-inline-input economics-inline-input-wide"
                    value={phase.label || ''}
                    onChange={(e) => updatePhase(index, { label: e.target.value })}
                    disabled={g.enabled === false || g.mode === 'channel'}
                  />
                </td>
                <td>
                  <input
                    className="economics-inline-input"
                    type="number"
                    min="1"
                    max="60"
                    value={phase.throughMonth}
                    onChange={(e) =>
                      updatePhase(index, {
                        throughMonth: Math.min(
                          60,
                          Math.max(1, parseInt(e.target.value, 10) || 1)
                        ),
                      })
                    }
                    disabled={g.enabled === false || g.mode === 'channel'}
                  />
                </td>
                <td>
                  <input
                    className="economics-inline-input"
                    type="number"
                    step="0.5"
                    value={phase.monthlyGrowthPct}
                    onChange={(e) =>
                      updatePhase(index, {
                        monthlyGrowthPct: Math.min(
                          1000,
                          Math.max(-100, parseFloat(e.target.value) || 0)
                        ),
                      })
                    }
                    disabled={g.enabled === false || g.mode === 'channel'}
                  />
                </td>
                <td>
                  <input
                    className="economics-inline-input"
                    type="number"
                    step="1"
                    value={phase.volatilityPct}
                    onChange={(e) =>
                      updatePhase(index, {
                        volatilityPct: Math.min(
                          200,
                          Math.max(0, parseFloat(e.target.value) || 0)
                        ),
                      })
                    }
                    disabled={g.enabled === false || g.mode === 'channel'}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="chart-hint">
        Bear / Base / Bull packs default to Fall flat / Traction / Explode. Switch packs on
        Scenarios, or override here for the active pack.
      </p>
    </section>
  );
}
